import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { AdminFilterBar } from "../../components/admin/AdminFilterBar";
import { adminHeaders, api } from "../../lib/api";
import { isLocalDevFrontend } from "../../lib/env";
import { money, shortDate } from "../../lib/format";

interface MemberRow {
  id: number;
  member_account: string;
  identity_code: string;
  status: string;
  status_label: string;
  bound_at: string;
  is_test: number;
  activation_deposit_id?: number | null;
}

interface MemberDetail {
  member: MemberRow;
  activation_deposit?: { amount?: number | string } | null;
  step1_reward?: { reward_status?: string } | null;
  step2_reward?: { reward_status?: string } | null;
  step3_reward?: { reward_status?: string } | null;
  step4_reward?: { reward_status?: string } | null;
  step5_reward?: { reward_status?: string } | null;
  step6_reward?: { reward_status?: string } | null;
  newbie_progress?: {
    step1_status?: string | null;
    step2_status?: string | null;
    step3_status?: string | null;
    step4_status?: string | null;
    step5_status?: string | null;
    step6_status?: string | null;
    step7_status?: string | null;
  } | null;
}

const statusOptions = [
  ["", "全部"],
  ["pending_channel", "等待確認"],
  ["channel_confirmed", "渠道已確認"],
  ["newbie_active", "新人七關進行中"],
  ["graduated", "正式成員 / 新人畢業"],
  ["channel_mismatch", "渠道不符"],
  ["identity_mismatch", "異常"],
  ["suspended", "異常"]
] as const;

export function AdminMembersPage() {
  const isLocalDev = isLocalDevFrontend();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [progressByAccount, setProgressByAccount] = useState<Record<string, { progress: string; activationAmount: string }>>({});
  const [searchAccount, setSearchAccount] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState("");

  async function loadAll() {
    setError("");
    const memberRows = await api<MemberRow[]>("/api/admin/members", { headers: adminHeaders() });
    setMembers(memberRows);
    const pairs = await Promise.all(memberRows.map(async (member) => {
      try {
        const detail = await api<MemberDetail>(`/api/admin/members/${encodeURIComponent(member.member_account)}`, { headers: adminHeaders() });
        return [member.member_account, {
          progress: memberProgressLabelV2(detail),
          activationAmount: detail.activation_deposit?.amount ? money(detail.activation_deposit.amount) : "-"
        }] as const;
      } catch {
        return [member.member_account, { progress: member.status_label, activationAmount: "-" }] as const;
      }
    }));
    setProgressByAccount(Object.fromEntries(pairs));
  }

  useEffect(() => {
    loadAll().catch((err) => setError(err instanceof Error ? err.message : "會員資料讀取失敗"));
  }, []);

  const filteredMembers = useMemo(() => {
    const accountKeyword = searchAccount.trim().toLowerCase();
    return members.filter((member) => {
      const accountMatch = !accountKeyword || member.member_account.toLowerCase().includes(accountKeyword);
      const statusMatch = !statusFilter || member.status === statusFilter;
      return accountMatch && statusMatch;
    });
  }, [members, searchAccount, statusFilter]);

  return (
    <AdminLayout title="會員管理">
      <section className="admin-card">
        <div className="admin-card-head"><span>MEMBER SEARCH</span><h2>會員查詢</h2></div>
        <AdminFilterBar>
          <label>會員帳號<input value={searchAccount} onChange={(event) => setSearchAccount(event.target.value)} placeholder={isLocalDev ? "test003" : "請輸入會員帳號"} /></label>
          <label>狀態篩選<select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>{statusOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
          <button onClick={loadAll}>查詢</button>
          <button className="secondary" onClick={() => { setSearchAccount(""); setStatusFilter(""); }}>重設</button>
        </AdminFilterBar>
      </section>

      {error ? <p className="error-box">{error}</p> : null}

      <section className="admin-card">
        <div className="admin-card-head"><span>MEMBER LIST</span><h2>會員列表</h2></div>
        <MemberTable rows={filteredMembers} progressByAccount={progressByAccount} />
      </section>
    </AdminLayout>
  );
}

function MemberTable({ rows, progressByAccount }: { rows: MemberRow[]; progressByAccount: Record<string, { progress: string; activationAmount: string }> }) {
  if (!rows.length) return <p>目前沒有會員資料</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>會員帳號</th>
            <th>手機後 3 碼</th>
            <th>狀態</th>
            <th>綁定時間</th>
            <th>目前進度</th>
            <th>入盟啟動筆</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const progress = progressByAccount[row.member_account];
            return (
              <tr key={row.id}>
                <td>{row.member_account}</td>
                <td>{row.identity_code}</td>
                <td>{row.status_label}</td>
                <td>{shortDate(row.bound_at)}</td>
                <td>{progress?.progress ?? row.status_label}</td>
                <td>{progress?.activationAmount ?? "-"}</td>
                <td><a className="admin-table-link" href={`/admin/members/${encodeURIComponent(row.member_account)}`}>查看會員詳情</a></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function memberProgressLabelV2(detail: MemberDetail) {
  if (detail.member.status === "graduated" || detail.newbie_progress?.step7_status === "completed") return "第 7 關已完成 / 新人畢業";
  if (detail.step6_reward?.reward_status === "completed" || detail.newbie_progress?.step6_status === "completed") return "第 6 關已完成 / 第 7 關可完成";
  if (detail.step6_reward?.reward_status === "ready" || detail.newbie_progress?.step6_status === "ready") return "第 6 關等待派發";
  if (detail.step5_reward?.reward_status === "completed" || detail.newbie_progress?.step5_status === "completed") return "第 5 關已完成 / 第 6 關可進行";
  if (detail.step5_reward?.reward_status === "ready" || detail.newbie_progress?.step5_status === "ready") return "第 5 關等待派發";
  if (detail.step4_reward?.reward_status === "completed" || detail.newbie_progress?.step4_status === "completed") return "第 4 關已完成 / 第 5 關可進行";
  if (detail.step4_reward?.reward_status === "ready" || detail.newbie_progress?.step4_status === "ready") return "第 4 關等待派發";
  if (detail.step3_reward?.reward_status === "completed" || detail.newbie_progress?.step3_status === "completed") return "第 3 關已完成 / 第 4 關可進行";
  if (detail.step3_reward?.reward_status === "ready") return "第 3 關等待派發";
  if (detail.step2_reward?.reward_status === "completed" || detail.newbie_progress?.step2_status === "completed") return "第 2 關已完成 / 第 3 關可進行";
  if (detail.step1_reward?.reward_status === "completed" || detail.newbie_progress?.step1_status === "completed") return "第 1 關已完成 / 第 2 關可進行";
  if (detail.step1_reward?.reward_status === "ready") return "第 1 關等待派發";
  return detail.member.status_label;
}
